import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Section, Chapter } from "./types";
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatCheckboxChange } from "@angular/material/checkbox";
import {v4 as uuidv4} from 'uuid'

interface Indexes {
  previousIndex: number
  currentIndex: number
}

interface State {
  newSectionCounter: number
  sections: Section[]
}

@Component({
  selector: 'kt-draggable',
  templateUrl: './draggable.component.html',
  styleUrls: ['./draggable.component.scss'],
  providers: [ComponentStore]
})
export class DraggableComponent implements OnDestroy {
  @Input()
  set sections(value: Section[] | null) {
    if (!value) return;
    this.store.patchState({sections: value})
  }

  @Output() sectionsChanged = new EventEmitter<Section[]>()

  private _onDestroy$ = new Subject<void>()

  sections$ = this.store.select(state => state.sections)

  constructor (
    private store: ComponentStore<State>
  ) {
    this.store.setState({
      newSectionCounter: 0,
      sections: []
    })

    // Effects
    this.sections$.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(sections => this.sectionsChanged.emit(sections))
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  sectionTrackBy(index: number, _item: any) {
    return index 
  }
  chapterTrackBy(index: number, _item: any) {
    return index
  }

  predicate(item: CdkDrag<Chapter>, drop: CdkDropList<Section>) {
    // Check for valuation summary
    const chapterNames = [
      'Valuation Overview',
      'Property Description',
      'Qualifications'
    ];

    if (chapterNames.includes(item.data.defaultName)) {
      if (drop.data.defaultName === 'Valuation Summary') return true
      return false;
    }
    return true;
  }

  /** Events */
  onDrop(event: CdkDragDrop<Section[]>) {
    this.dropSection(event)
  }
  onChapterDrop(event: CdkDragDrop<Chapter[]>) {
    if (event.container === event.previousContainer) {
      this.dropChapter({...event, sectionId: event.container.id})
    } else {
      this.dropChapterBetweenSections({
        ...event,
        sectionId: event.container.id,
        previousSectionId: event.previousContainer.id
      })
    }
  }
  onSectionCheckboxChange(event: MatCheckboxChange, sectionId: string) {
    this.toggleSection({id: sectionId, value: event.checked})
  }
  onSectionNameChange(event: any, sectionId: string) {
    this.changeSectionName({value: event.target.value, id: sectionId})
  }
  onSectionNameResetToDefault(sectionId: string) {
    this.resetSectionName(sectionId)
  }
  onChapterCheckboxChange(event: MatCheckboxChange, sectionId: string, chapterId: string) {
    this.toggleChapter({value: event.checked, sectionId, id: chapterId})
  }
  onChapterNameChange(event: any, sectionId: string, chapterId: string) {
    this.changeChapterName({value: event.target.value, sectionId: sectionId, id: chapterId})
  }
  onChapterNameResetToDefault(sectionId: string, chapterid: string) {
    this.resetChapterName({sectionId, id: chapterid})
  }
  onDeleteSection(sectionId: string) {
    this.deleteSection(sectionId)
  }

  /** Commands */
  addNewSection() {
    this.store.patchState((state) => ({
      sections: [{
        id: uuidv4(),
        name: `New Section (${state.newSectionCounter + 1})`,
        included: true,
        chapters: []
      } ,...state.sections],
      newSectionCounter: state.newSectionCounter + 1
    }))
  }

  resetSections(sections: Section[]) {
    this.store.patchState({
      sections,
      newSectionCounter: 0
    })
  }

  private readonly dropSection = this.store.updater(
    (state, {previousIndex, currentIndex}: Indexes) => {
      let sections = [...state.sections]
      moveItemInArray(sections, previousIndex, currentIndex)
      return {
        ...state,
        sections: sections
      }
    }
  )

  private readonly dropChapter = this.store.updater(
    (state, {sectionId, previousIndex, currentIndex}: {sectionId: string} & Indexes) => {
    return {
      ...state,
      sections: state.sections.map(section => {
        if (section.id !== sectionId) return section
        let currentChapters = [...section.chapters]
        moveItemInArray(currentChapters, previousIndex, currentIndex)
        return {
          ...section,
          chapters: currentChapters
        }
      })
    }
  })

  private readonly dropChapterBetweenSections = this.store.updater(
    (state, { sectionId, previousSectionId, previousIndex, currentIndex}: Indexes & {sectionId: string, previousSectionId: string}) => {
    let sections = [...state.sections]
    const previousSectionIndex = sections.findIndex(section => section.id === previousSectionId)
    if (previousSectionIndex === -1) return state
    const currentSectionIndex = sections.findIndex(section => section.id === sectionId) 
    if (currentSectionIndex === -1) return state

    let previousChapters = [...sections[previousSectionIndex].chapters]
    let currentChapters = [...sections[currentSectionIndex].chapters]
    transferArrayItem(previousChapters, currentChapters, previousIndex, currentIndex)
    const previousSection = {
      ...sections[previousSectionIndex],
      chapters: previousChapters
    }
    const currentSection = {
      ...sections[currentSectionIndex],
      chapters: currentChapters
    }

    sections[previousSectionIndex] = previousSection
    sections[currentSectionIndex] = currentSection

    return {
      ...state,
      sections
    }
  })

  private readonly changeSectionName = this.store.updater(
    (state, {value, id}: {value: string, id: string}) => {
      return {
        ...state,
        sections: state.sections.map(section => {
          if (section.id !== id) return section;
          return {
            ...section,
            name: value
          }
        })
      }
    }
  )

  private readonly resetSectionName = this.store.updater(
    (state, id: string) => {
      return {
        ...state,
        sections: state.sections.map(section => {
          if (section.id !== id) return section;
          return {
            ...section,
            name: section.defaultName
          }
        })
      }
    }
  )

  private readonly changeChapterName = this.store.updater(
    (state, {value, id, sectionId}: {value: string, sectionId: string, id: string}) => {
      return {
        ...state,
        sections: state.sections.map(section => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            chapters: section.chapters.map(chapter => {
              if (chapter.id !== id) return chapter
              return {
                ...chapter,
                name: value
              }
            })
          }
        })
      }
    }
  )

  private readonly resetChapterName = this.store.updater(
    (state, {sectionId, id}: {sectionId: string, id: string}) => {
      return {
        ...state,
        sections: state.sections.map(section => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            chapters: section.chapters.map(chapter => {
              if (chapter.id !== id) return chapter
              return {
                ...chapter,
                name: chapter.defaultName
              }
            })
          }
        })
      }
    }
  )

  private readonly toggleSection = this.store.updater(
    (state, {id, value}: {id: string, value: boolean}) => {
      return {
        ...state,
        sections: state.sections.map(
          section => {
            if (section.id !== id) return section
            return {
              ...section,
              included: value,
              chapters: section.chapters.map(chapter => ({
                ...chapter,
                included: value
              }))
            }
          }
        )
      }
    }
  )

  private readonly toggleChapter = this.store.updater(
    (state, { sectionId, id, value }: {value: boolean, id: string, sectionId: string}) => {
      return {
        ...state,
        sections: state.sections.map(
          section => {
            if (section.id !== sectionId) return section
            return {
              ...section,
              included: value == true ? value : section.included,
              chapters: section.chapters.map(
                chapter => {
                  if (chapter.id !== id) return chapter
                  return {
                    ...chapter,
                    included: value
                  }
                }
              )
            }
          }
        )
      }
    }
  )

  private readonly deleteSection = this.store.updater(
    (state, sectionId: string) => {
      return {
        ...state,
        sections: state.sections.filter(
          section => section.id !== sectionId
        )
      }
    }
  )
}